import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  // 添加状态来存储最近的听写记录
  const [recentDictations, setRecentDictations] = useState([]);
  
  // 从localStorage获取听写记录
  useEffect(() => {
    const fetchRecentDictations = () => {
      try {
        // 从localStorage获取听写结果
        const dictationResults = JSON.parse(localStorage.getItem('dictationResults') || '[]');
        
        // 按日期排序（最新的在前）
        const sortedResults = dictationResults.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        // 只取最近的5条记录
        const recentResults = sortedResults.slice(0, 3);
        
        setRecentDictations(recentResults);
      } catch (error) {
        console.error('获取听写记录失败:', error);
        setRecentDictations([]);
      }
    };
    
    fetchRecentDictations();
  }, []);
  
  // 格式化日期显示
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '今天';
      }
      
      const now = new Date();
      const isToday = date.getDate() === now.getDate() && 
                      date.getMonth() === now.getMonth() && 
                      date.getFullYear() === now.getFullYear();
      
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = date.getDate() === yesterday.getDate() && 
                          date.getMonth() === yesterday.getMonth() && 
                          date.getFullYear() === yesterday.getFullYear();
      
      if (isToday) {
        return '今天';
      } else if (isYesterday) {
        return '昨天';
      } else {
        return `${date.getMonth() + 1}月${date.getDate()}日`;
      }
    } catch (error) {
      console.error('日期格式化错误:', error);
      return '未知时间';
    }
  };

  // 获取听写类型的图标和颜色
  const getDictationTypeInfo = (dictation) => {
    // 根据bookInfo中的信息判断听写类型
    if (dictation.bookInfo?.difficulty && dictation.bookInfo.difficulty.includes('难度')) {
      // 包含难度信息的是随机选择
      return {
        icon: 'fa-shuffle',
        color: 'text-purple-500',
        bgColor: 'bg-purple-100',
        label: '随机'
      };
    } else if (dictation.bookInfo?.unit && dictation.bookInfo.unit.includes('AI')) {
      // 包含AI的是AI短文听写
      return {
        icon: 'fa-wand-magic-sparkles',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100',
        label: 'AI'
      };
    } else {
      // 默认为手动选择
      return {
        icon: 'fa-book',
        color: 'text-blue-500',
        bgColor: 'bg-blue-100',
        label: '手动'
      };
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#333]">
      {/* Main Content */}
      <div className="p-5 pb-24">
        {/* App Header */}
        <div className="text-center mb-8 pt-4">
          <div className="text-blue-500 text-5xl mb-2">
            <i className="fa-solid fa-headphones"></i>
          </div>
          <h1 className="text-3xl font-bold mb-1">爱上听写</h1>
          <p className="text-gray-500 text-sm">快乐听写 · 轻松学习</p>
        </div>

        {/* Quick Start Buttons */}
        <div className="space-y-4 mb-8">
          <Link to="/manual-selection" className="bg-blue-500 text-white rounded-xl p-5 shadow-md flex items-center">
            <div className="rounded-full bg-white/20 p-3 mr-4">
              <i className="fa-solid fa-book text-2xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">手动选择</h3>
              <p className="text-sm text-white/80">从课本单元中选择单词进行听写</p>
            </div>
            <i className="fa-solid fa-chevron-right"></i>
          </Link>

          <Link to="/random-selection" className="bg-purple-500 text-white rounded-xl p-5 shadow-md flex items-center">
            <div className="rounded-full bg-white/20 p-3 mr-4">
              <i className="fa-solid fa-shuffle text-2xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">随机选择</h3>
              <p className="text-sm text-white/80">随机生成单词组进行听写</p>
            </div>
            <i className="fa-solid fa-chevron-right"></i>
          </Link>
          
          <Link to="/ai-dictation" className="bg-indigo-600 text-white rounded-xl p-5 shadow-md flex items-center relative">
            <div className="absolute -top-2 -right-2 bg-yellow-400 text-indigo-900 text-xs font-bold px-2 py-1 rounded-full">
              新功能
            </div>
            <div className="rounded-full bg-white/20 p-3 mr-4">
              <i className="fa-solid fa-wand-magic-sparkles text-2xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">AI短文听写</h3>
              <p className="text-sm text-white/80">AI生成有趣短文，融入选择的单词</p>
            </div>
            <i className="fa-solid fa-chevron-right"></i>
          </Link>
        </div>

        {/* Recent History */}
        <div className="mb-8">
          <h3 className="font-bold text-lg mb-3">最近听写</h3>
          
          <div className="space-y-3">
            {recentDictations.length > 0 ? (
              recentDictations.map((dictation, index) => {
                const typeInfo = getDictationTypeInfo(dictation);
                return (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-sm flex justify-between items-center relative">
                  {/* 添加类型标签到右上角 */}
                  <div className={`${typeInfo.bgColor} ${typeInfo.color} text-xs px-2 py-1 rounded-full absolute -top-2 right-2`}>
                    {typeInfo.label}
                  </div>
                  <div className="flex items-center">
                    <div>
                      <h4 className="font-semibold">
                        {dictation.bookInfo?.grade || ''}
                        {dictation.bookInfo?.term || ''} 
                        {dictation.bookInfo?.unit || ''}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {dictation.totalCount || 0}个单词 · {formatDate(dictation.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-green-500">
                    <span className="font-bold">{dictation.correctRate || 0}%</span>
                  </div>
                </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl p-4 shadow-sm text-center text-gray-500">
                暂无听写记录
              </div>
            )}
          </div>
        </div>

        {/* Quick Tips */}
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <h3 className="font-bold text-yellow-700 flex items-center">
            <i className="fa-solid fa-lightbulb mr-2"></i>小贴士
          </h3>
          <p className="text-sm text-yellow-700">
            坚持每天听写15分钟，记忆效果会更好哦！
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;